import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ProviderService } from '../../provider.service';
import { ComputAssistApiService } from '../../computassist-api.service';
import { MedicationPrescription, PersonVo } from '../../api-client.service';
import {
  COMPUTASSIST_ORDER_SUFFIX,
  COMPUTASSIST_STATUS_LABELS,
  ComputAssistStatusCode,
  ComputAssistOrderStatusRecord,
  ComputAssistLineOrigin,
  ComputAssistLineResponse,
  buildComputAssistTimelineSteps,
  describeComputAssistLineOrigin,
} from '../../models/computassist.model';
import {
  PharmacyEscriptDescriptor,
  EscriptStatusRecord,
  EscriptRequestLine,
  EscriptSendContext,
  EscriptSendResult,
  EscriptTimelineStep,
  UnifiedStatusView,
} from '../pharmacy-escript.descriptor';
import { UnifiedPharmacy, computAssistToUnified } from '../unified-pharmacy.model';
import { FirestoreStreamStatusSource } from '../sources/firestore-stream-status.source';

/** ComputAssist vendor descriptor - webhook stream status, client-generated id, financial + line items. */
@Injectable({ providedIn: 'root' })
export class ComputAssistDescriptor implements PharmacyEscriptDescriptor {
  readonly id = 'computassist';
  readonly displayName = 'ComputAssist';
  readonly chip = { kind: 'text' as const, value: 'CA', color: '#5b53c6' };
  readonly sentField = 'SentToComputAssist';
  readonly identifierField = 'ComputAssistScriptNumber';
  readonly orderNumberSuffix = COMPUTASSIST_ORDER_SUFFIX;
  readonly statusMode = 'stream' as const;
  readonly capabilities = { canCancel: false, showFinancials: true, showLineItems: true };
  readonly ingest = {
    kind: 'csv' as const,
    columns: {
      ramsno: 'ramsno',
      storeName: 'storeName',
      address1: 'address1',
      address2: 'address2',
      address3: 'address3',
      postal: 'postal',
    },
    required: ['ramsno', 'storeName'],
    toUnified: computAssistToUnified,
  };

  constructor(
    private providerService: ProviderService,
    private caApi: ComputAssistApiService,
    private streamSource: FirestoreStreamStatusSource
  ) {}

  allowed$(): Observable<boolean> {
    return this.providerService.IsAllowedComputAssistScripts$;
  }

  readScriptId(prescription: MedicationPrescription): string | null {
    return (prescription.SentToComputAssist && prescription.SentToComputAssist.scriptNumber) || this.caApi.getScriptNumber(prescription);
  }

  /** CA rejects scripts without an identifier for BOTH patient and main member (CA call, 25 Aug 2026) - the fields feeding `patient.idno` / `member.mainidno`. */
  validateSend(prescription: MedicationPrescription): { title: string; message: string } | null {
    const patient = prescription.Patient?.PatientDetails;
    const mainMember = prescription.Patient?.PatientAccountDetails?.MedicalAidMainMemberDetails;
    const missing: string[] = [];
    if (!this.hasIdentifier(patient)) {
      missing.push('patient');
    }
    if (!this.hasIdentifier(mainMember)) {
      missing.push('main member');
    }
    const who = missing.join(' and ');
    if (who) {
      return {
        title: `${who.charAt(0).toUpperCase()}${who.slice(1)} ID or passport number required`,
        message:
          `ComputAssist pharmacies cannot accept an electronic prescription without the ${who}\u2019s ID or passport number. ` +
          'Please add this information to the patient\u2019s details and try again, or select a different pharmacy.',
      };
    }

    // Surface the send-time NAPPI pack-code gate up front, before Send is attempted (was an internal
    // throw the clinician never saw - swallowed into a generic "failed to send" toast).
    const incompleteLine = this.caApi.findIncompletePackCodeLine(prescription);
    if (incompleteLine) {
      return {
        title: 'Medicine pack size missing',
        message:
          `"${incompleteLine.MedicationDescription ?? 'This medicine'}" is missing pack size information and cannot be sent to ` +
          'ComputAssist. Remove it from the prescription and re-add it from the medicine list, then try again.',
      };
    }

    return null;
  }

  private hasIdentifier(person?: PersonVo | null): boolean {
    return !!(person?.IdentityNo || person?.PassportNo);
  }

  async send(ctx: EscriptSendContext): Promise<EscriptSendResult> {
    const scriptNumber = this.caApi.getScriptNumber(ctx.prescription);
    await this.caApi.sendScriptToComputAssist(ctx.tenantId, ctx.prescription, ctx.pharmacy.raw, ctx.pdfUrl, ctx.encounterId);
    return { identifier: scriptNumber };
  }

  buildSentPayload(result: EscriptSendResult, pharmacy: UnifiedPharmacy): any {
    return { scriptNumber: result.identifier, pharmacy: pharmacy.raw, Status: 'Sent' };
  }

  status$(practiceId: string, identifier: string): Observable<EscriptStatusRecord | null> {
    return this.streamSource.stream$('ComputAssistOrders', identifier, doc => this.normalize(doc));
  }

  /**
   * A successful send returns code 0 "accepted", i.e. ComputAssist has RECEIVED the script (same
   * reasoning as Dis-Chem's code-1 receipt). The streamed order doc carries no status until the
   * webhook lands, so default the pre-webhook state to ReceivedByPharmacy (-1).
   */
  private normalize(doc: any): EscriptStatusRecord | null {
    if (!doc) {
      return null;
    }
    const sentAt = doc.createdAt ?? doc.statusUpdatedAt ?? new Date().toISOString();
    return {
      ...doc,
      statusCode: doc.statusCode ?? ComputAssistStatusCode.ReceivedByPharmacy,
      statusUpdatedAt: doc.statusUpdatedAt ?? sentAt,
    };
  }

  toStatusView(record: EscriptStatusRecord | null): UnifiedStatusView | null {
    if (!record) {
      return null;
    }
    const code = record.statusCode;
    if (code === undefined || code === null || code === ComputAssistStatusCode.ReceivedByPharmacy) {
      return {
        statusCode: ComputAssistStatusCode.ReceivedByPharmacy,
        statusLabel: COMPUTASSIST_STATUS_LABELS[ComputAssistStatusCode.ReceivedByPharmacy],
        pillColor: 'orange',
      };
    }
    const pillColor: 'orange' | 'green' | 'red' =
      code === ComputAssistStatusCode.Processed ? 'green' : code === ComputAssistStatusCode.Rejected ? 'red' : 'orange';
    return { statusCode: code, statusLabel: COMPUTASSIST_STATUS_LABELS[code] ?? `Status ${code}`, pillColor };
  }

  buildTimelineSteps(record: EscriptStatusRecord): EscriptTimelineStep[] {
    return buildComputAssistTimelineSteps(record as ComputAssistOrderStatusRecord) as EscriptTimelineStep[];
  }

  lineStatusLabel(status: string): string {
    return COMPUTASSIST_STATUS_LABELS[Number(status)] ?? `Status ${status}`;
  }

  lineStatusColor(status: string): 'orange' | 'green' | 'red' {
    const code = Number(status);
    if (code === ComputAssistStatusCode.Processed) {
      return 'green';
    }
    if (code === ComputAssistStatusCode.Rejected) {
      return 'red';
    }
    return 'orange';
  }

  lineOrigin(line: ComputAssistLineResponse, record: EscriptStatusRecord): ComputAssistLineOrigin {
    return describeComputAssistLineOrigin(line, record as ComputAssistOrderStatusRecord);
  }

  requestLinesFor(prescription: MedicationPrescription): EscriptRequestLine[] {
    return this.caApi.requestLines(prescription);
  }
}
